import { useEffect, useState } from "react";
import { Calendar as CalIcon, Clock, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function StepWhen({ dates, date, setDate, slots, timeSlot, setTimeSlot }) {
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    if (!date) {
      setAvailability(null);
      return;
    }
    const iso = toISODate(date);
    let cancelled = false;
    api.get(`/availability?date=${iso}`)
      .then((r) => { if (!cancelled) setAvailability(r.data); })
      .catch(() => { if (!cancelled) setAvailability(null); });
    return () => { cancelled = true; };
  }, [date]);

  const slotInfo = (s) => availability?.slots?.find((x) => x.slot === s);

  return (
    <div data-testid="step-when">
      <h2 className="font-heading text-2xl tracking-tight mb-6 flex items-center gap-2"><CalIcon size={20} />Choose a date</h2>
      <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-10 gap-2">
        {dates.map((d) => {
          const iso = toISODate(d);
          const active = date && toISODate(date) === iso;
          return (
            <button
              key={iso}
              onClick={() => { setDate(d); setTimeSlot(""); }}
              className={`p-3 rounded-xl border text-center transition ${
                active ? "bg-forest text-white border-forest" : "bg-white border-black/10 hover:border-forest/40"
              }`}
              data-testid={`date-${iso}`}
            >
              <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">
                {d.toLocaleDateString("en-US", { weekday: "short" })}
              </div>
              <div className="font-heading text-lg">{d.getDate()}</div>
              <div className="text-[10px] opacity-70">{d.toLocaleDateString("en-US", { month: "short" })}</div>
            </button>
          );
        })}
      </div>

      <h2 className="font-heading text-2xl tracking-tight mt-10 mb-6 flex items-center gap-2"><Clock size={20} />Pick a time</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        {slots.map((s) => {
          const info = slotInfo(s);
          const full = info?.full;
          const limited = info && !full && info.remaining <= 1;
          const isSelected = timeSlot === s;
          return (
            <button
              key={s}
              onClick={() => !full && setTimeSlot(s)}
              disabled={full}
              className={`relative p-4 rounded-xl border text-center font-heading transition ${
                full
                  ? "bg-bone border-black/5 text-muted2/60 cursor-not-allowed"
                  : isSelected
                  ? "bg-forest text-white border-forest"
                  : "bg-white border-black/10 hover:border-forest/40"
              }`}
              data-testid={`slot-${s.replace(/[\s:]/g, "")}`}
            >
              <div>{s}</div>
              {info && (
                <div className={`text-[10px] uppercase tracking-[0.18em] mt-1 ${
                  full ? "text-red-700" : limited ? "text-clay" : isSelected ? "text-white/80" : "text-muted2"
                }`}>
                  {full ? "Full" : `${info.remaining} left`}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {availability && availability.slots.every((s) => s.full) && (
        <div className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-xl flex items-center gap-2" data-testid="all-slots-full">
          <AlertTriangle size={14} /> All slots are booked for this date. Please pick another day.
        </div>
      )}
    </div>
  );
}
